import React, { useState } from 'react';
import './AccuracyLegend.css';
import { Settings, X, ChevronDown, ChevronUp } from './Icons';

const AccuracyLegend = ({ 
  minAccuracy, 
  maxAccuracy, 
  onBoundsChange 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [minValue, setMinValue] = useState(2);
  const [maxValue, setMaxValue] = useState(50);

  React.useEffect(() => {
    setMinValue(minAccuracy);
    setMaxValue(maxAccuracy);
  }, [minAccuracy, maxAccuracy]);

  const handleApply = () => {
    const min = parseFloat(minValue);
    const max = parseFloat(maxValue);
    
    if (!isNaN(min) && !isNaN(max) && min < max) {
      onBoundsChange(min, max);
      setIsEditing(false);
    } else {
      alert('Invalid bounds: Min must be less than Max');
    }
  };

  const handleReset = () => {
    // Reset to default bounds (2-50)
    onBoundsChange(2, 50);
    setIsEditing(false);
  };

  const range = maxAccuracy - minAccuracy;
  const thresholds = {
    excellent: minAccuracy,
    good: minAccuracy + range * 0.2,
    fair: minAccuracy + range * 0.4,
    poor: minAccuracy + range * 0.6,
    veryPoor: minAccuracy + range * 0.8,
    max: maxAccuracy
  };

  return (
    <div className={`accuracy-legend ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="legend-header">
        <h4
          className="legend-title-toggle"
          onClick={() => setIsCollapsed(prev => !prev)}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          GPS Accuracy
          {isCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </h4>
        {!isCollapsed && (
          <button 
            onClick={() => setIsEditing(!isEditing)}
            className="edit-button"
            title={isEditing ? "Close editor" : "Adjust accuracy bounds"}
          >
            {isEditing ? <X size={16} /> : <Settings size={16} />}
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>
          {isEditing ? (
            <div className="accuracy-editor">
              <div className="editor-row">
                <label>Min (Excellent):</label>
                <input
                  type="number"
                  value={minValue}
                  onChange={(e) => setMinValue(e.target.value)}
                  step="0.1"
                />
              </div>
              <div className="editor-row">
                <label>Max (Very Poor):</label>
                <input
                  type="number"
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                  step="0.1"
                />
              </div>
              <div className="editor-actions">
                <button onClick={handleApply} className="apply-button">
                  Apply
                </button>
                <button onClick={handleReset} className="reset-button">
                  (2-50)
                </button>
              </div>
            </div>
          ) : (
            <div className="legend-items">
              <div className="legend-item">
                <span className="legend-color" style={{background: '#70C314'}}></span>
                <span className="legend-label">Excellent</span>
                <span className="legend-value">&lt; {thresholds.good.toFixed(1)}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{background: '#FAEB36'}}></span>
                <span className="legend-label">Good</span>
                <span className="legend-value">{thresholds.good.toFixed(1)} - {thresholds.fair.toFixed(1)}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{background: '#FFA500'}}></span>
                <span className="legend-label">Fair</span>
                <span className="legend-value">{thresholds.fair.toFixed(1)} - {thresholds.poor.toFixed(1)}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{background: '#E81416'}}></span>
                <span className="legend-label">Poor</span>
                <span className="legend-value">{thresholds.poor.toFixed(1)} - {thresholds.veryPoor.toFixed(1)}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{background: '#111111'}}></span>
                <span className="legend-label">Very Poor</span>
                <span className="legend-value">&gt; {thresholds.veryPoor.toFixed(1)}</span>
              </div>
            </div>
          )}

          <div className="legend-note">
            Data range: {minAccuracy.toFixed(2)} - {maxAccuracy.toFixed(2)}
          </div>
        </>
      )}
    </div>
  );
};

export default AccuracyLegend;

